import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["recordForm", "recordInput", "recordLabel", "uploadForm", "uploadInput"]
  static values = {
    recordLabel: String,
    stopLabel: String,
    unsupportedMessage: String
  }

  connect() {
    this.mediaRecorder = null
    this.chunks = []
    this.stream = null
    this.recording = false
  }

  disconnect() {
    this.stopTracks()
  }

  openPicker() {
    this.uploadInputTarget.click()
  }

  submitUpload() {
    this.uploadFormTarget.requestSubmit()
  }

  async toggleRecording() {
    if (this.recording) {
      this.recording = false
      this.mediaRecorder.stop()
      return
    }

    if (!this.recordingSupported()) {
      this.handleRecordingUnavailable()
      return
    }

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      this.mediaRecorder = new MediaRecorder(this.stream)
    } catch (_error) {
      this.handleRecordingUnavailable()
      return
    }

    this.chunks = []

    this.mediaRecorder.addEventListener("dataavailable", (event) => {
      if (event.data.size > 0) this.chunks.push(event.data)
    })

    this.mediaRecorder.addEventListener("stop", () => {
      const blob = new Blob(this.chunks, { type: this.mediaRecorder.mimeType || "audio/webm" })
      const file = new File([blob], "voice-note.webm", { type: blob.type })
      void this.submitRecordedFile(file)
    })

    this.mediaRecorder.start()
    this.recording = true
    this.recordLabelTarget.textContent = this.stopLabelValue
  }

  recordingSupported() {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      return false
    }

    return typeof MediaRecorder !== "undefined"
  }

  handleRecordingUnavailable() {
    this.stopTracks()
    this.recording = false
    if (this.hasRecordLabelTarget) {
      this.recordLabelTarget.textContent = this.recordLabelValue
    }
    window.alert(this.unsupportedMessageValue)
  }

  stopTracks() {
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop())
      this.stream = null
    }
  }

  async submitRecordedFile(file) {
    try {
      if (typeof DataTransfer !== "undefined") {
        const transfer = new DataTransfer()
        transfer.items.add(file)
        this.recordInputTarget.files = transfer.files
        this.recordFormTarget.requestSubmit()
        return
      }

      const formData = new FormData(this.recordFormTarget)
      formData.set("audio_file", file, file.name)
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

      const response = await fetch(this.recordFormTarget.action, {
        method: (this.recordFormTarget.method || "post").toUpperCase(),
        body: formData,
        credentials: "same-origin",
        headers: {
          Accept: "text/html, application/xhtml+xml",
          ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {})
        }
      })

      if (response.redirected) {
        window.location.assign(response.url)
        return
      }

      window.location.reload()
    } finally {
      this.stopTracks()
      this.recording = false
      if (this.hasRecordLabelTarget) {
        this.recordLabelTarget.textContent = this.recordLabelValue
      }
    }
  }
}
